import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';
import { User, UserRoles } from '../../shared/core/state/users/models';
import { Subscription, filter } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import {
  TenantService,
  TenantInfo,
} from '../../shared/services/tenant.service';
import { DashboardSidebarService } from '../../shared/services/dashboard-sidebar.service';
import * as UserActions from '../../shared/core/state/users/user.actions';
import { Resource } from '../../shared/core/state/resource/models';

@Component({
  selector: 'black-dashboard-header',
  templateUrl: './dashboard-header.component.html',
  standalone: false,
})
export class DashboardHeaderComponent implements OnInit, OnDestroy {
  private store = inject<Store<State>>(Store);
  private router = inject(Router);
  public tenantService = inject(TenantService);
  public sidebarService = inject(DashboardSidebarService);
  private cdr = inject(ChangeDetectorRef);

  user: Partial<User> | null = null;
  currentTenant: TenantInfo | null = null;
  desktopCollapsed = false;
  currentTitle = 'Dashboard';

  get isThemeToggleAvailable(): boolean {
    return this.tenantService.isThemeToggleAvailable;
  }

  get isDarkMode(): boolean {
    return this.tenantService.isDarkMode;
  }

  toggleTheme(): void {
    this.tenantService.toggleTheme();
  }

  private subscription = new Subscription();

  ngOnInit(): void {
    this.subscription.add(
      this.store
        .select((state) => state.users.user)
        .subscribe((user) => (this.user = user)),
    );

    this.subscription.add(
      this.tenantService.currentTenant$.subscribe((tenant) => {
        this.currentTenant = tenant;
      }),
    );

    this.subscription.add(
      this.sidebarService.desktopCollapsed$.subscribe((collapsed) => {
        this.desktopCollapsed = collapsed;
        this.cdr.markForCheck();
      }),
    );

    this.updateTitle(this.router.url);

    this.subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.updateTitle(event.urlAfterRedirects);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateTitle(url: string): void {
    if (url.includes('/dashboard/overview')) {
      this.currentTitle = 'Overview';
    } else if (url.includes('/dashboard/instructor')) {
      this.currentTitle = 'My Classes';
    } else if (url.includes('/dashboard/app-management')) {
      this.currentTitle = 'Integrations';
    } else if (url.includes('/dashboard/earnings')) {
      this.currentTitle = 'Earnings';
    } else if (
      url.includes('/dashboard/orders') ||
      url.includes('/dashboard/order')
    ) {
      this.currentTitle = 'Orders';
    } else if (url.includes('/dashboard/subscriptions')) {
      this.currentTitle = 'Subscriptions';
    } else if (url.includes('/dashboard/invoices')) {
      this.currentTitle = 'Invoices';
    } else if (url.includes('/dashboard/profile/tutor-profile')) {
      this.currentTitle = 'Tutor Profile';
    } else if (url.includes('/dashboard/profile/tutor-settings')) {
      this.currentTitle = 'Tutor Profile Editor';
    } else if (url.includes('/dashboard/profile')) {
      this.currentTitle = 'My Profile';
    } else if (url.includes('/dashboard/learning')) {
      this.currentTitle = 'My Classes';
    } else if (url.includes('/dashboard/my-courses')) {
      this.currentTitle = 'My Courses';
    } else if (url.includes('/dashboard/meetings')) {
      this.currentTitle = 'Meetings';
    } else if (url.includes('/dashboard/past-recordings')) {
      this.currentTitle = 'Past Recordings';
    } else if (url.includes('/dashboard/batches')) {
      this.currentTitle = 'Batches';
    } else if (url.includes('/dashboard/my-batches')) {
      this.currentTitle = 'My Batches';
    } else if (url.includes('/dashboard/academy-settings')) {
      this.currentTitle = 'Academy Settings';
    } else {
      this.currentTitle = 'Dashboard';
    }
  }

  get fullName(): string {
    if (this.user?.name) {
      return `${this.user.name.firstName} ${this.user.name.lastName}`;
    }
    return 'User';
  }

  get userInitial(): string {
    if (this.user?.name?.firstName) {
      return this.user.name.firstName.charAt(0).toUpperCase();
    }
    return 'U';
  }

  get isInstructor(): boolean {
    return this.user?.role === UserRoles.INSTRUCTOR;
  }

  get isInstructorOrAdmin(): boolean {
    return (
      this.user?.role === UserRoles.INSTRUCTOR ||
      this.user?.role === UserRoles.ADMIN
    );
  }

  get showTutorProfile(): boolean {
    return !this.tenantService.isSoloInstructor();
  }

  get hasIncompleteTutorProfile(): boolean {
    if (!this.isInstructorOrAdmin) return false;
    const tInfo = {
      ...(this.user?.profile?.tutorInfo || {}),
      ...(this.user?.pendingTutorProfileUpdates?.tutorInfo || {}),
    };
    const bio =
      this.user?.pendingTutorProfileUpdates?.personal?.biography ||
      this.user?.profile?.personal?.biography;
    return (
      !tInfo ||
      !tInfo.subjects?.length ||
      !tInfo.whatsAppNumber ||
      !bio ||
      bio.trim() === ''
    );
  }

  getAvatarUrl(avatar: Resource | undefined): string | null {
    return avatar?.path || null;
  }

  toggleDesktopSidebar(): void {
    this.sidebarService.toggleDesktopCollapsed();
  }

  toggleMobileSidebar(): void {
    this.sidebarService.toggleMobile();
  }

  logout(): void {
    this.store.dispatch(UserActions.logout({ redirect: true }));
  }
}
